/**
 * Guarda a origem da visita para que ela sobreviva ao login e ao redirecionamento.
 *
 * Entre o clique no anúncio (e-mail, aviso no navegador) e a candidatura acontecem
 * login e troca de rota, e a query some no caminho. Quem lê depois é a tela de
 * candidatura, que grava a origem em `applications_sources`.
 */
const UTM_KEYS = ["utm_source", "utm_campaign", "utm_medium"];

/**
 * Query string vira string vazia quando o parâmetro não veio, e `undefined` vira
 * a palavra "undefined" ao ser gravada — um valor que parece preenchido e
 * contamina a atribuição da candidatura.
 *
 * Repetido na URL (`?utm_source=indeed&utm_source=indeed`), o parâmetro chega
 * do vue-router como array. Vale o primeiro valor aproveitável: numa duplicata
 * é o mesmo valor, e em valores diferentes é o que veio primeiro no link. O
 * array cru viajava no corpo do POST e derrubava a gravação da candidatura no
 * backend.
 */
export function sanitizeUtmValue(value) {
  if (Array.isArray(value)) {
    for (const item of value) {
      const text = sanitizeUtmValue(item);
      if (text) return text;
    }
    return null;
  }

  if (value === undefined || value === null) return null;

  const text = String(value).trim();
  if (!text || text === "undefined" || text === "null") return null;

  return text;
}

/**
 * A query inteira já sanitizada, para desestruturar no lugar de `route.query`.
 *
 * Quem monta o corpo da candidatura lê vários parâmetros de uma vez
 * (`utm_source`, `utm_medium`, `utm_campaign`, `clickid`) e nenhum deles tem
 * garantia de vir uma vez só: os links são escritos por job board, disparo de
 * e-mail e quem mais divulgar a vaga.
 */
export function sanitizeQuery(query = {}) {
  return Object.keys(query).reduce((acc, key) => {
    acc[key] = sanitizeUtmValue(query[key]);
    return acc;
  }, {});
}

/** Guarda as utms presentes na query, sem apagar as que já estavam lá. */
export function persistUtms(query = {}) {
  UTM_KEYS.forEach((key) => {
    const value = sanitizeUtmValue(query[key]);
    if (!value) return;

    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      // sessionStorage indisponível: a utm da query ainda vale nesta navegação.
    }
  });
}

/** Lê uma utm guardada, já sem os valores-lixo das versões anteriores. */
export function readUtm(key) {
  try {
    return sanitizeUtmValue(sessionStorage.getItem(key));
  } catch (e) {
    return null;
  }
}

/** As três utms guardadas, para montar o corpo da candidatura. */
export function readUtms() {
  return {
    utm_source: readUtm("utm_source"),
    utm_campaign: readUtm("utm_campaign"),
    utm_medium: readUtm("utm_medium"),
  };
}
